import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { exportarParaExcel } from '../../../utils/exportarExcel';
import Filter from "../components/Filter";
import ReleaseTable from "../components/ReleaseTable";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { getPageName, getUiInterface } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useCargoSearchList } from '../../../apis/cargoSearchList/CargoSearchListProvider';
import i18n from '../../../i18n';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { useRelease } from "../../../apis/release/ReleaseProvider";
import { downloadContent } from '../../../services/contentService';
import { getListaRelease } from '../../../services/releaseService';
import AlertOverlay from '../../../components/Alert';
import CargoContentList from '../../../components/CargoContentList';
import DocumentPreviewModal from '../../../components/DocumentPreviewModal';
import { useDocumentPreview } from '../../../hooks/useDocumentPreview';
import { Content } from "../types";

// Quantidade grande o bastante para trazer, na pratica, "todos" os registros que
// batem com o filtro numa unica pagina - o export nao pode ficar preso aos 40 da
// grid, senao um filtro com mais resultados exportaria so uma fatia sem avisar.
const QUANTIDADE_PARA_EXPORTAR_TUDO = '100000';

// A API so aplica o filtro de PU quando inicio E fim estao presentes
// (CargoDAO.FindByDinamicFilter exige os dois). Para expressar "PU >= 01/01/2021"
// sem limite superior, o fim usa uma data bem no futuro - nunca alcancada na pratica.
const PU_FILTRO_DATA_MINIMA = '2021-01-01';
const PU_FILTRO_DATA_MAXIMA_SEM_LIMITE = '2099-12-31';

const ReleaseListView: React.FC = () => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState<{
      message: string;
      variant: "success" | "danger" | "warning" | "info";
  } | null>(null);
  const { apiResponse, releases, fetchReleases } = useRelease();
  const { cargosSearchList, fetchCargoSearchList } = useCargoSearchList();
  const { previewState, openPreview, closePreview } = useDocumentPreview();
  const [isLoading, setIsLoading] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  // Ligado por padrao: a tela sempre abre mostrando so processos recentes.
  const [filtrarPuApartir2021, setFiltrarPuApartir2021] = useState(true);
  const [filters, setFilters] = useState<Record<string, string>>({
    from: '',
    arrivalDate: '',
    puInicio: PU_FILTRO_DATA_MINIMA,
    puFim: PU_FILTRO_DATA_MAXIMA_SEM_LIMITE,
    pageNum: "0",
    quantityPerPage: "40"
  });

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 40,
    page: 0,
  });

  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

  useEffect(() => {
    fetchCargoSearchList();
  }, [fetchCargoSearchList]);

  useEffect(() => {
    const fetchData = async () => {
      if (cargosSearchList && cargosSearchList.length > 0) {
        const initialFilters = Object.fromEntries(
          cargosSearchList.map(({ key }: { key: string }) => [key, ""])
        );

        // Campos de data que precisam de período (case-insensitive)
        const dateFieldsLower = ['arrivaldate', 'pu', 'input'];
        
        // Adicionar campos de período para cada campo de data encontrado
        const dateFilters: Record<string, string> = {
          arrivalDateInicio: '',
          arrivalDateFim: '',
        };
        
        cargosSearchList.forEach(({ key }: { key: string }) => {
          if (dateFieldsLower.includes(key.toLowerCase()) && key.toLowerCase() !== 'arrivaldate') {
            dateFilters[`${key}Inicio`] = '';
            dateFilters[`${key}Fim`] = '';
          }
        });

        const updatedFilters = {
          ...initialFilters,
          ...dateFilters,
          from: '',
          arrivalDate: '',
          puInicio: filtrarPuApartir2021 ? PU_FILTRO_DATA_MINIMA : '',
          puFim: filtrarPuApartir2021 ? PU_FILTRO_DATA_MAXIMA_SEM_LIMITE : '',
          pageNum: "0",
          quantityPerPage: "40"
        };

        setFilters(updatedFilters);
      }
    };

    fetchData();
    // filtrarPuApartir2021 entra aqui com seguranca: e um booleano que so muda por
    // acao do usuario, nao por este efeito, entao nao cria loop (ao contrario de
    // filters/fetchReleases no efeito abaixo, que sao excluidos de proposito).
  }, [cargosSearchList, filtrarPuApartir2021]);

  useEffect(() => {
    const newFilters = {
      ...filters,
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString(),
      sort: sortModel[0]?.field ?? '',
      order: sortModel[0]?.sort ?? '',
    };
    setFilters(newFilters);
    fetchReleases(newFilters);
  }, [paginationModel, sortModel]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    const newFilters = { 
      ...filters, 
      pageNum: "0",
      quantityPerPage: "40"
    };
    setFilters(newFilters);
    fetchReleases(newFilters);
  };

  /**
   * Liga/desliga o filtro de PU sem exigir um clique em "Pesquisar" -
   * e um checkbox, a resposta precisa ser imediata.
   */
  const handleToggleFiltrarPu = (checked: boolean) => {
    setFiltrarPuApartir2021(checked);
    setFilters((prev) => ({
      ...prev,
      puInicio: checked ? PU_FILTRO_DATA_MINIMA : '',
      puFim: checked ? PU_FILTRO_DATA_MAXIMA_SEM_LIMITE : '',
    }));
    // Muda a referencia de paginationModel para acionar o efeito de busca,
    // voltando para a primeira pagina do resultado filtrado.
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  /** Converte uma linha da grid nas mesmas colunas que a tela mostra. */
  const montaLinhaParaExportar = (row: any) => {
    const formataData = (valor: unknown) => {
      if (!valor) return '';
      const data = new Date(valor as string);
      return isNaN(data.getTime()) ? '' : dateFormatter.format(data);
    };

    const info = row?.carrierInformation?.[0];

    return {
      [t('release.fields.from')]: row.fromIdentification ?? '',
      [t('release.fields.client')]: row.clientName ?? '',
      [t('release.fields.supplier')]: row.supplierName ?? '',
      [t('release.fields.wrNumber')]: row.wrNumber ?? '',
      [t('release.fields.poNumber')]: row.poNumber ?? '',
      [t('release.fields.pieces')]: row.pieces ?? '',
      [t('release.fields.weight')]: row.weight ?? '',
      [t('release.fields.dawb')]: row.dawb ?? '',
      [t('release.fields.invoice')]: info?.invoice ?? '',
      [t('release.fields.pu')]: formataData(row.pu),
      [t('release.fields.input')]: formataData(row.input),
      [t('release.fields.content')]: (row.cargoContents ?? [])
        .map((c: any) => c.description)
        .filter(Boolean)
        .join(', '),
    };
  };

  /**
   * Exporta para Excel todos os itens que batem com o filtro atual - nao so a
   * pagina visivel na grid. Busca direto do servico, com uma pagina grande o
   * bastante para trazer tudo de uma vez, sem alterar a lista exibida na tela.
   */
  const handleExportarExcel = async () => {
    setExportandoExcel(true);

    try {
      const filtrosParaExportar = {
        ...Object.fromEntries(reorderedFilters),
        pageNum: '0',
        quantityPerPage: QUANTIDADE_PARA_EXPORTAR_TUDO,
      };

      const resposta = await getListaRelease(filtrosParaExportar, false);
      const linhas = (resposta?.data?.values ?? []).map(montaLinhaParaExportar);

      if (linhas.length === 0) {
        setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
        return;
      }

      await exportarParaExcel(linhas, 'liberacoes', 'Releases');
    } catch (error) {
      setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
    } finally {
      setExportandoExcel(false);
    }
  };

  const handleDownload = async (content: Pick<Content, 'id' | 'description'>) => {
    try {
      const response = await downloadContent(content.id);
      
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${content.description}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        setAlert({ message: `${t('release.labels.downloadError')} ${content.description}`, variant: "danger" });
    }
  };

  // E5: visualizar PDF/imagem direto no navegador, sem baixar antes.
  const handleView = async (content: Pick<Content, 'id' | 'description'>) => {
    try {
      await openPreview(content);
    } catch (error) {
      setAlert({ message: `${t('release.labels.downloadError')} ${content.description}`, variant: "danger" });
    }
  };

  const dateFormatter = new Intl.DateTimeFormat(i18n?.language ?? 'pt');

  const columns: GridColDef[] = [
    { field: 'fromIdentification', headerName: t('release.fields.from'), width: 80 },
    { field: 'clientName', headerName: t('release.fields.client'), width: 150 },
    { field: 'supplierName', headerName: t('release.fields.supplier'), width: 260 },
    { field: 'wrNumber', headerName: t('release.fields.wrNumber'), width: 120 },
    { field: 'poNumber', headerName: t('release.fields.poNumber'), width: 120 },
    {
      field: 'piecesWeight',
      headerName: t('release.fields.piecesWeight'),
      width: 120,
      renderCell: (params: any) => {
        const { pieces, weight } = params.row;
        return `${pieces} / ${weight}`;
      },
    },
    { field: 'dawb', headerName: t('release.fields.dawb'), width: 150 },
    {
      field: 'invoice',
      headerName: t('release.fields.invoice'),
      width: 220,
      renderCell: (params: any) => {
        const info = params?.row?.carrierInformation?.[0];
        const invoice = info?.invoice ?? '-';
        const documentKey = info?.document?.key ?? '-';
        return `${invoice} / ${documentKey}`;
      }
    },
    {
      field: 'pu',
      headerName: t('release.fields.pu'),
      type: "date",
      width: 150,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      }
    },
    {
      field: 'input',
      headerName: t('release.fields.input'),
      type: "date",
      width: 150,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      }
    },
    {
      field: 'content',
      headerName: t('release.fields.content'),
      width: 500,
      minWidth: 200,
      renderCell: (params: any) => (
        <CargoContentList
          cargoContents={params.row?.cargoContents ?? []}
          onDownload={handleDownload}
          downloadTitle={t('release.labels.clickToDownload')}
          expandTitle={t('release.labels.showAllFiles')}
          onView={handleView}
          viewTitle={t('release.labels.clickToView')}
        />
      )
    }
  ];

  const dynamicFilters = Object.entries(filters).filter(
    ([key]) => key !== 'from' && key !== 'arrivalDate'
  );

  const reorderedFilters = [
    ...dynamicFilters,
    ['from', filters.from],
    ['arrivalDate', filters.arrivalDate],
  ];

  const navItems: NavBarItem[] = [
    { label: t('release.labels.home'), route: "/", iconName: "house-door" },
    { label: t('release.labels.releasedList'), route: "/release/released", iconName: "table" },
  ];

  return (
     <Container
      fluid
      style={{
        height: 'calc(100vh - 150px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <NavBar items={navItems} />
      {alert && (
          <AlertOverlay
              message={alert.message}
              variant={alert.variant}
              onClose={() => setAlert(null)}
          />
      )}
      <div className="mb-3"></div>
      <h2>{getPageName("release")}</h2>
      <div className="mb-3"></div>
      <Filter
        filters={Object.fromEntries(reorderedFilters)}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        filtrarPuApartir2021={filtrarPuApartir2021}
        onToggleFiltrarPuApartir2021={handleToggleFiltrarPu}
      />
      <div className="mt-1" style={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <p>{t('release.labels.loadingData')}</p>
        ) : (
          <ReleaseTable
            apiResponse={apiResponse}
            rows={releases ?? []}
            columns={columns}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            onExportExcel={handleExportarExcel}
            exportandoExcel={exportandoExcel}
          />
        )}
      </div>

      <DocumentPreviewModal state={previewState} onClose={closePreview} />
    </Container>
  );
};

export default ReleaseListView;
